import { Link } from 'react-router-dom';
import { Rocket } from 'lucide-react';
import ServiceStoryPage from '../../components/ServiceStoryPage';

const color = 'hsl(243 75% 59%)';

export const seo = {
  title: 'Vision to Launch',
  tagline: 'From idea to fully launched business',
  metaDesc: 'Vision to Launch takes you from concept to a fully branded, operational business — identity, social branding, SEO/AEO foundations and company registration.',
  slug: 'vision-to-launch',
  whatWeDoItems: [
    { heading: 'A brand that looks the part', body: 'A cohesive, professional look — logo, colours, fonts and simple guidelines — so your business looks established from day one.' },
    { heading: 'Design across the essentials', body: 'Branded artwork for the places that matter — social profiles, cover images and the materials you’ll actually use.' },
    { heading: 'Social profiles set up right', body: 'Your social accounts created and branded consistently, so you make a strong first impression wherever people find you.' },
    { heading: 'Found on Google from the start', body: 'The building blocks of being discovered — keyword research, sensible structure and clear messaging for search and AI answer engines.' },
    { heading: 'A clear message', body: 'We help you put into plain words what you do, who it’s for, and why it matters — so people instantly get it.' },
    { heading: 'Your tech, all connected', body: 'Google Business Profile, social accounts, email, accounting tools — the platforms your business needs, set up and working together.' },
    { heading: 'Company registration help', body: 'Friendly guidance through the Companies House process so your business is set up correctly and legally from the off.' },
    { heading: 'A launch checklist', body: 'A clear, step-by-step checklist and hands-on guidance so nothing important slips through the cracks on launch day.' },
  ],
};

export default function VisionToLaunchPage() {
  return (
    <ServiceStoryPage
      tagline={seo.tagline}
      Icon={Rocket}
      color={color}
      ctaText="Book a free call"
      heroHeadline="Go from a good idea to open for business — set up properly"
      heroSub="We take you from concept to a fully branded, ready-to-run business — your identity, your online presence, your accounts and your launch foundations, all handled. You focus on the idea; we handle the complexity."
      problem={{
        heading: 'You\u2019re starting something new — and the setup feels overwhelming',
        body: [
          'You\u2019re building a brand-new business from scratch and there\u2019s a hundred things to sort before you can even open the doors — branding, a website, social accounts, Google, registering the company — with no obvious order to do them in. It\u2019s easy to freeze before you start.',
          'You want to look established from day one, and you really don\u2019t want to make an expensive setup mistake you\u2019ll have to unpick later. But piecing it together from YouTube videos and half-finished DIY tools is slow, stressful, and lonely — and doing it right the first time saves you years of untangling.',
        ],
        footer: (
          <>
            Already up and running with tools everywhere?{' '}
            <Link to="/services/software-audit" className="underline" style={{ color }}>
              See our Software Audit & Consolidation
            </Link>{' '}
            instead.
          </>
        ),
      }}
      whatYouGetIntro="Everything you need to launch looking professional and set up correctly — brand, presence and foundations, all done for you."
      whatYouGet={seo.whatWeDoItems}
      howItWorks={[
        { title: 'A chat about your idea', body: 'We start with a free, friendly call to understand your idea, your goals and where you want to take it.' },
        { title: 'Brand & foundations', body: 'We build your identity, messaging and the SEO groundwork so everything looks and reads like a real, established business.' },
        { title: 'We set everything up', body: 'Website, social profiles, Google, email, accounts and integrations — connected and working together, ready to go.' },
        { title: 'Launch, step by step', body: 'We walk you through a clear launch checklist so nothing gets missed and you go live with confidence.' },
      ]}
      proofHeading="From zero to a real, growing business"
      proofSub="Eclipse Paving came to us with no online presence at all. We built their brand and site, set up their accounts, and got them found — the same foundations we’ll put in place for you."
      pricing={{
        price: 'From £950+',
        caption: 'Guide price — scoped to what your launch needs, with a fixed quote before any work begins.',
      }}
      finalHeading="Ready to launch properly?"
      finalSub="Book a free call and we’ll map out exactly what your launch needs — so you start with a strong brand, a clear message and every foundation in place."
    />
  );
}
